import type { I18n, VueI18n } from '@nuxtjs/i18n/dist/runtime/composables';
import type { Price } from '~/types/plugins/subscriptions';
import { useDisplay, useLocale } from 'vuetify';
export function formatNumbers(value: string | number) {
  const { $vuetify, $i18n } = useNuxtApp();
  const isRtl = $vuetify.locale.isRtl.value;
  if (value == 'NaN:NaN') {
    value = '00:00';
  }
  const arabicNumbers = ['۰', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
  let chars = value?.toString().split('');
  for (let i = 0; i < chars?.length; i++) {
    if (/\d/.test(chars[i])) {
      if ($i18n.locale.value === 'ar' || isRtl) {
        chars[i] = arabicNumbers[+chars[i]];
      } else {
        return value;
      }
    }
  }
  return chars?.join('');
}

export default defineNuxtPlugin(() => {
  function releaseYear(value: string | number) {
    if (value) {
      return new Date(value).getFullYear();
    } else {
      return '';
    }
  }
  function durationAsDigital(value: string | number) {
    if (value == 'NaN:NaN') {
      value = '00:00';
    }
    const data = new Date(+value * 1000).toISOString();
    if (+value < 3600) {
      return data.substring(14, 14 + 5);
    } else {
      return data.substring(11, 11 + 8);
    }
  }
  function durationToString(value: string) {
    const { $vuetify, $i18n } = useNuxtApp();
    if (!value) return '00:00';
    const duration = parseInt(value, 10);
    if (duration < 0) {
      return '00:00';
    }

    const hourStr = $i18n.t('duration.hours');
    const minStr = $i18n.t('duration.minutes');
    const secStr = $i18n.t('duration.sec');

    const hoursDuration = Math.floor(duration / 3600);
    let minutesDuration: number | string = Math.floor(
      (duration - hoursDuration * 3600) / 60,
    );
    minutesDuration =
      minutesDuration <= 9 ? `0${minutesDuration}` : minutesDuration;
    let secondsDuration: number | string =
      duration - hoursDuration * 3600 - +minutesDuration * 60;
    secondsDuration = Math.round(secondsDuration * 100) / 100;
    secondsDuration =
      secondsDuration <= 9 ? `0${secondsDuration}` : secondsDuration;

    const resultDuration = `${hoursDuration ? hoursDuration + hourStr + ' ' : ' '}${minutesDuration}${!hoursDuration ? ':' + secondsDuration + minStr : minStr}`;

    return resultDuration;
  }

  function formatContentRating(value: string | number) {
    const { $vuetify, $i18n } = useNuxtApp();
    const isRtl = $vuetify.locale.isRtl.value;
    if (!value || typeof value !== 'string') {
      return value;
    }
    if ($i18n.locale.value === 'ar' || isRtl) {
      return value.replace(/PG/g, `${$i18n.t('PG')} `);
    } else {
      return value;
    }
  }

  function formatSeconds(value: number) {
    const data = new Date(value * 1000).toISOString();
    if (value < 3600) {
      return data.substring(14, 14 + 5);
    } else {
      return data.substring(11, 11 + 8);
    }
  }

  function byteToMb(byte: number) {
    return `${Math.ceil(byte / 1024 / 1024)}MB`;
  }

  function priceFormatter(price: Price, separator: string) {
    return (
      price &&
      price.amountMain +
        (price.amountExt ? (separator || '.') + price.amountExt : '')
    );
  }

  function cleanTitle(value: string) {
    return (
      value &&
      typeof value === 'string' &&
      value.replace('_', ' ').replaceAll('-', ' ')
    );
  }

  function fullDate(date: string | number | Date) {
    return date && new Date(date).toDateString();
  }

  function localeFullDate(date: string | number | Date) {
    const { $vuetify, $i18n } = useNuxtApp();
    let options: {} = {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    };
    let localeDate;
    if ($i18n.locale.value === 'ar') {
      localeDate = date && new Date(date).toLocaleDateString('ar-EG', options);
    } else {
      localeDate = date && new Date(date).toLocaleDateString('en-US', options);
    }
    return typeof localeDate == 'string' && localeDate?.replace(/,/g, '');
  }

  function shortDate(date: string | number | Date) {
    const { $vuetify, $i18n } = useNuxtApp();
    let options: {} = {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
    };
    let localeDate;
    if ($i18n.locale.value === 'ar') {
      localeDate = date && new Date(date).toLocaleDateString('ar-EG', options);
    } else {
      localeDate = date && new Date(date).toLocaleDateString('ro-RO', options);
    }
    return typeof localeDate == 'string' && localeDate?.replace(/,/g, '');
  }

  function localTime(date: string | number | Date) {
    return date && new Date(date).toLocaleTimeString();
  }

  function localTimeWithoutSeconds(date: string | number | Date) {
    return (
      date &&
      new Date(date).toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
      })
    );
  }
  function isoTimeWithoutSeconds(date: string | number | Date) {
    if (!date) return '';
    // Get the UTC time in HH:MM format
    const d = new Date(date);
    const hours = d.getUTCHours().toString().padStart(2, '0');
    const minutes = d.getUTCMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  }
  const filters = {
    releaseYear,
    durationAsDigital,
    durationAsDigitalFormated(value: any) {
      return formatNumbers(durationAsDigital(value));
    },
    durationToString,
    durationToStringFormated(value: any) {
      return durationToString(value);
    },
    formatContentRating,
    formatContentRatingFormated(value: any) {
      return formatContentRating(formatNumbers(value));
    },
    formatNumbers,
    formatSeconds,
    byteToMb,
    byteToMbFormated(value: any) {
      return formatNumbers(byteToMb(value));
    },
    priceFormatter,
    cleanTitle,
    fullDate,
    localeFullDate,
    shortDate,
    localTime,
    localTimeWithoutSeconds,
    isoTimeWithoutSeconds,
    releaseYearFormated(value: any) {
      return formatNumbers(releaseYear(value));
    },
  };
  return {
    provide: {
      filters,
    },
  };
});
